import React from 'react';
import { DropdownButton, MenuItem, Glyphicon, Button } from 'react-bootstrap';
import { DragSource, DropTarget, connectDragSource  } from 'react-dnd';
import PageActions from '../../actions/page';
import PageStore from '../../stores/page';
import ComponentWrapper from '../../components/page/component-wrapper';
import { Containers, UnknownContainer } from '../../config/containers';
import Model from '../../utils/model';
import '../../../css/page/container-wrapper.less';
import { isDescendant } from '../../utils/pageContainers';

// Rendering a ContainerWrapper from within the render function of ContainerWrapper
// causes a bug with the drag n drop decorators since upgrading babel from 5 to 6.
// Rendering the nested ContainerWrapper in a separate function is working.
const renderContainerWrapper = props => <ContainerWrapper {...props} />;

const containerSource = {
    beginDrag (props, monitor, component) {
        return {
            containerType: props.container.containerType,
            developerName: props.container.developerName,
            id: props.container.id,
            order: props.container.order,
            pageContainerId: props.container.parent
        };
    },
    endDrag (props, monitor, component) {
        if (monitor.didDrop())
            props.onEndDrag();
    },
    canDrag (props, monitor) {
        return !props.disabled && !props.editing.id && props.highlight === props.container.id;
    },
    isDragging (props, monitor) {
        return props.container.id == monitor.getItem().id;
    }
};

export const containerTarget = {

    canDrop (props, monitor) {
        const element = monitor.getItem();
        if (props.container.id === element.id) {
            return false;
        }
        // Prevent dropping into a descendant of itself
        if (isDescendant(element.id, props.container.id, props.allContainers)) {
            return false;
        }

        const container = Containers.find(item => manywho.utils.isEqual(item.id, props.container.containerType, true));

        let acceptsKey = 'components';
        let typeKey = 'componentType';

        if (element.hasOwnProperty('containerType')) {
            acceptsKey = 'containers';
            typeKey = 'containerType';
        }

        if (container.accepts[acceptsKey] instanceof RegExp) {
            return container.accepts[acceptsKey].test(element[typeKey]);
        }
        
        if (container && Array.isArray(container.accepts[acceptsKey]) && container.accepts[acceptsKey].filter(accept => manywho.utils.isEqual(accept, element[typeKey], true)).length > 0) {
            return true;
        }

        return false;
    },

    hover (props, monitor, container) {
        if (monitor.canDrop() && monitor.isOver({ shallow: true })) {

            const children = props.components
                .concat(container.props.container.pageContainers || [])
                .filter((element) => element.pageContainerId === props.container.id || element.parent === props.container.id)
                .sort((element1, element2) => parseInt(element1.order) - parseInt(element2.order));

            if (children && children.length > 0) {

                let axis = Containers.filter((item) => item.id == props.container.containerType)[0].axis;

                if (!axis)
                    axis = UnknownContainer.axis;

                const mousePosition = monitor.getClientOffset()[axis];
                const upperBounds = axis === 'y' ? 'top' : 'left';
                const lowerBounds = axis === 'y' ? 'bottom' : 'right';

                // Get all components below mouse
                const components = children.filter((child) => {
                    const childBoundingRect = document.getElementById('element-' + child.id).getBoundingClientRect();

                    return !(mousePosition < childBoundingRect[upperBounds]);
                });

                let lastComponentBoundingRect = null;
                let lastComponentMiddle = null;
                let drop = null;

                const lastComponent = components[components.length - 1];
                const dragComponent = monitor.getItem();

                if (lastComponent) {
                    lastComponentBoundingRect = document.getElementById('element-' + lastComponent.id).getBoundingClientRect();
                    lastComponentMiddle = (lastComponentBoundingRect[lowerBounds] - lastComponentBoundingRect[upperBounds]) / 2;

                    // Below every component
                    if (
                        components.length === children.length &&
                        mousePosition > lastComponentBoundingRect[upperBounds] + lastComponentMiddle
                    ) {

                        drop = { id: lastComponent.id, order: lastComponent.order, side: lowerBounds };
                    }
                }

                // Above every component
                if (drop === null && components.length === 0) {

                    drop = { id: children[0].id, order: parseInt(children[0].order), side: upperBounds };
                }
                else {

                    let side = null;

                    // Overlapping a component
                    if (mousePosition >= lastComponentBoundingRect[upperBounds] && mousePosition <= lastComponentBoundingRect[lowerBounds])
                        side = (mousePosition - lastComponentBoundingRect[upperBounds]) > lastComponentMiddle ? lowerBounds : upperBounds;
                    // Between two components
                    else
                        side = lowerBounds;

                    drop = { id: lastComponent.id, order: lastComponent.order, side: side };
                }

                if (drop)
                    container.setState({ drop: drop });
            }
            // Container has no components
            else {
                container.setState({ highlight: true });
            }
        }
    },

    drop (props, monitor, container) {
        if (monitor.didDrop() || (container.state.drop && monitor.getItem().id === container.state.drop.id))
            return;

        let order = 0;

        let axis = Containers.filter((item) => item.id == props.container.containerType)[0].axis;

        if (!axis)
            axis = UnknownContainer.axis;

        const lowerBounds = axis === 'y' ? 'bottom' : 'right';
        const dropped = monitor.getItem();

        if (container.state.drop !== null || container.state.highlight) {
            if (props.container.id === dropped.pageContainerId && parseInt(container.state.drop.order) - 1 === parseInt(dropped.order) && container.state.drop.side !== lowerBounds)
                return;

            const isBefore = container.state.drop && !manywho.utils.isEqual(container.state.drop.side, lowerBounds, true);

            if (dropped.id && dropped.id !== 'placeholder') {
                PageActions.moveItem(props.pageId, dropped, dropped.pageContainerId, props.container.id, container.state.drop ? container.state.drop.id : null, isBefore);
			}
            else {
                let order = 0;
                if (container.state && container.state.drop)
                    order = container.state.drop.side === lowerBounds ? parseInt(container.state.drop.order) + 1 : parseInt(container.state.drop.order);

                dropped.pageContainerId = props.container.id;
                dropped.id = Model.guid();

                container.setState({ placeholderOrder: order });
                setTimeout(() => PageActions.editComponent('new', dropped, order, props.pageId));
            }
        }

    }
};

@DragSource('component', containerSource, (connect, monitor) => ({
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging()
    })
)
@DropTarget('component', containerTarget, (connect, monitor) => ({
        connectDropTarget: connect.dropTarget(),
        isOver: monitor.isOver(),
        isOverCurrent: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
        clientOffset: monitor.getClientOffset()
    })
)

class ContainerWrapper extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            drop: null,
        };
        this.onClick = this.onClick.bind(this);
        this.onEndDrag = this.onEndDrag.bind(this);
        this.onTypeSelect = this.onTypeSelect.bind(this);
        this.onMoveUp = this.onMoveUp.bind(this);
        this.onMoveDown = this.onMoveDown.bind(this);
    }

    onClick(e) {
        e.stopPropagation();

        if (!this.props.page.editing.id && this.props.page.highlight !== this.props.container.id)
            PageActions.setHighlightedElement(this.props.container.id, this.props.pageId);
    }

    onEndDrag() {
        this.setState({ drop: null });
    }

    onTypeSelect(eventKey, event) {
        PageActions.changeContainerType(this.props.container.id, Containers.filter(container => event.currentTarget.id === container.id)[0].id, this.props.pageId);
    }

    onMoveUp(e) {
        let order = parseInt(this.props.container.order);
        PageActions.moveItemUp(this.props.pageId, this.props.container, this.props.container.parent, order);
    }

    onMoveDown(e) {
        let order = parseInt(this.props.container.order);
        PageActions.moveItemDown(this.props.pageId, this.props.container, this.props.container.parent, order);
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (this.state.drop !== nextState.drop) {
            return JSON.stringify(this.state.drop) !== JSON.stringify(nextState.drop);
        }
        return true;
    }

    render() {
        const { connectDragSource, connectDragPreview, isDragging, connectDropTarget, isOverCurrent, canDrop } = this.props;

        let children = [];
        let siblings = this.props.components.filter((component) => manywho.utils.isEqual(component.pageContainerId, this.props.container.id, true));
        let containerConfig = Containers.filter((item) => item.id == this.props.container.containerType)[0];

        if (!containerConfig)
            containerConfig = UnknownContainer;

        if (this.props.container && this.props.container.pageContainers) {
            siblings = siblings.concat(this.props.container.pageContainers);
            children = this.props.container.pageContainers;
        }

        if (this.props.components)
            children = children.concat(this.props.components.filter(component => manywho.utils.isEqual(component.pageContainerId, this.props.container.id, true)));

        if (this.props.page.droppedOn != null && this.props.page.droppedOn == this.props.container.id) {
            children = children.map(item => {
                if (parseInt(item.order) >= this.state.placeholderOrder)
                    item.order = parseInt(item.order) + 1;
                return item;
            });
            children.push({ componentType: 'mw-placeholder', order: this.state.placeholderOrder });
        }

        children = children
            .sort((a, b) => parseInt(a.order) - parseInt(b.order))
            .map((item) => {
                if (item.containerType)
                    return renderContainerWrapper({
                        page: this.props.page,
                        modelId: this.props.modelId,
                        pageId: this.props.pageId,
                        order: item.order,
                        key: item.id,
                        container: item,
                        components: this.props.components,
                        flowKey: this.props.flowKey,
                        side: isOverCurrent && this.state.drop && this.state.drop.id === item.id && this.state.drop.side,
                        editing: this.props.page.editing,
                        disabled: this.props.page.droppedOn !== null,
                        highlight: this.props.page.highlight,
                        onEndDrag: this.onEndDrag,
                        siblingNumber: siblings.length,
                        allContainers: this.props.allContainers,
                    });
                else if (item.componentType == 'mw-placeholder')
                    return <ComponentWrapper order={this.state.placeholderOrder} componentType="mw-placeholder" />
                else if (item.componentType)
                    return <ComponentWrapper modelId={this.props.modelId}
                                            pageId={this.props.pageId}
                                            key={item.id}
                                            component={item}
                                            order={item.order}
                                            axis={containerConfig.axis}
                                            disabled={this.props.page.droppedOn}
                                            editing={this.props.page.editing}
                                            siblingNumber={siblings.length}
                                            side={isOverCurrent && this.state.drop && this.state.drop.id === item.id && this.state.drop.side}
                                            onEndDrag={this.onEndDrag}
                                            highlight={this.props.page.highlight}
                                            flowKey={this.props.flowKey} />
            });

        let child = null;

        if (!manywho.utils.isEqual(containerConfig.id, 'unknown',  true))
            child = React.createElement(manywho.component.getByName(this.props.container.containerType), {
                flowKey: this.props.flowKey,
                id: this.props.container.id,
                children: children,
                isDesignTime: true
            });

        const remove = !this.props.isRoot && <Button disabled={this.props.highlight !== this.props.container.id} className="element-actions btn-nobackground" onClick={PageActions.removeContainer.bind(null, this.props.container.id, this.props.pageId)}><Glyphicon glyph="remove"></Glyphicon></Button>;

        const dragClasses = ['component-handle', 'bg-primary', 'element-actions'];
        const classes = manywho.styling.getClasses(this.props.container.parent, this.props.container.id, this.props.container.containerType, this.props.flowKey);
        classes.push('container-wrapper');

        if (this.props.page.droppedOn)
            dragClasses.push('disabled');

        if (this.props.page.highlight === this.props.container.id)
            classes.push('highlight');

        if (this.props.page.editing.id == this.props.container.id)
            classes.push('editing');

        if (isDragging)
            classes.push('semi-transparent');

        if (isOverCurrent && canDrop)
            classes.push('drag-hover');

        if (!isOverCurrent && this.props.side) {
            classes.push('drop-target');
            classes.push(`drop-${this.props.side}`);
        }

        const containerButtons = [];

        if (!this.props.page.droppedOn && !this.props.page.editing.id) {
            if (this.props.siblingNumber > 1 && parseInt(this.props.container.order) > 0)
                containerButtons.push(<Button key="container-move-up" disabled={this.props.highlight && this.props.highlight !== this.props.container.id} className="element-actions btn-nobackground" onClick={this.onMoveUp}><Glyphicon glyph="arrow-up"></Glyphicon></Button>);

            if (this.props.siblingNumber > 1 && parseInt(this.props.container.order) < this.props.siblingNumber - 1)
                containerButtons.push(<Button key="container-move-down" disabled={this.props.highlight && this.props.highlight !== this.props.container.id} className="element-actions btn-nobackground" onClick={this.onMoveDown}><Glyphicon glyph="arrow-down"></Glyphicon></Button>);

            containerButtons.push(<Button key="container-edit" disabled={this.props.highlight && this.props.highlight !== this.props.container.id} className="element-actions btn-nobackground" onClick={PageActions.editContainer.bind(null, 'edit', this.props.container, this.props.pageId)}><Glyphicon glyph="cog"></Glyphicon></Button>);
            if (this.props.container.parent) {
                // Do not allow cloning of the Main parent container
                containerButtons.push(<Button key="container-clone" disabled={this.props.highlight && this.props.highlight !== this.props.container.id} className="element-actions btn-nobackground" onClick={PageActions.cloneContainer.bind(null, this.props.container.id, this.props.pageId)}><Glyphicon glyph="duplicate"></Glyphicon></Button>);
            }

            containerButtons.push(<Button key="container-new" disabled={this.props.highlight && this.props.highlight !== this.props.container.id} className="element-actions btn-nobackground" onClick={PageActions.addContainer.bind(null, containerConfig.accepts.default, 'new container', children.length, this.props.container.id, this.props.pageId)}><Glyphicon glyph="plus"></Glyphicon></Button>);
        }

        return connectDragPreview(connectDropTarget(
            <div id={'element-' + this.props.container.id} className={classes.join(' ')} onClick={this.onClick}>
                <div className="container-wrapper-header">
                    { !this.props.isRoot && connectDragSource(<div className={dragClasses.join(' ')}><Glyphicon glyph="move"></Glyphicon></div>) }
                    <span className="container-name">{this.props.container.developerName}</span>
                    <div className="container-wrapper-actions">
                        {containerButtons}
                        <DropdownButton
                            pullRight
                            disabled={this.props.highlight && this.props.highlight !== this.props.container.id}
                            className="element-actions"
                            bsSize='xsmall'
                            title={containerConfig.name}
                            bsStyle="primary"
                            id={containerConfig.id + '-container-type'}
                            key={containerConfig.id}
                            onSelect={this.onTypeSelect}
                        >
                            { Containers.map((item, index) => <MenuItem key={item.id} id={item.id} eventKey={item.id}>{item.name}</MenuItem>) }
                        </DropdownButton>
                        {remove}
                    </div>
                </div>
                {child}
            </div>
        ));

    }
}

export default ContainerWrapper;
